import { Link } from "react-router-dom";
import {
  BadgeCheck, IndianRupee, Home, Timer, CalendarClock, Headset, Receipt, ShieldCheck, Globe2, ArrowRight, ClipboardCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PROFESSIONS, STEPS } from "@/lib/data";

export const SectionHead = ({ eyebrow, title, sub, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
    <div className="max-w-2xl">
      <span className="eyebrow">{eyebrow}</span>
      <h2 className="h2 mt-2">{title}</h2>
      {sub && <p className="mt-3 text-base md:text-lg text-muted-foreground">{sub}</p>}
    </div>
    {action}
  </div>
);

export const InspectionBanner = () => (
  <section className="container-x" data-testid="inspection-section">
    <div className="relative overflow-hidden rounded-[2rem] bg-primary text-primary-foreground p-8 sm:p-12 grid md:grid-cols-[auto_1fr] gap-8 items-center">
      <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10" aria-hidden />
      <div className="absolute right-24 -bottom-24 h-56 w-56 rounded-full bg-white/5" aria-hidden />
      <div className="relative rounded-3xl bg-white text-[#0B8F4D] px-8 py-6 text-center shadow-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.2em]">Inspection Charge</p>
        <p className="font-display text-5xl sm:text-6xl font-extrabold mt-1" data-testid="inspection-price">₹199</p>
        <p className="text-sm font-semibold">Only</p>
      </div>
      <div className="relative">
        <p className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider"><ClipboardCheck className="h-4 w-4" />Applicable for all service visits</p>
        <h2 className="mt-4 text-2xl sm:text-3xl font-bold">No surprises. A transparent quote before any work begins.</h2>
        <p className="mt-3 text-white/85 max-w-2xl">Technician visits your location, inspects the issue, and provides a transparent quotation before starting work.</p>
        <Button asChild variant="secondary" className="mt-6 rounded-full h-11 px-6 bg-white text-[#0B8F4D] hover:bg-white/90" data-testid="inspection-book-button">
          <Link to="/book">Book an Inspection <ArrowRight className="h-4 w-4 ml-1" /></Link>
        </Button>
      </div>
    </div>
  </section>
);

const WHY = [
  [BadgeCheck, "Verified Professionals", "ID-verified, background-checked experts."],
  [IndianRupee, "Transparent Pricing", "₹199 inspection, upfront quotation."],
  [Home, "Doorstep Service", "We come to you — home or office."],
  [Timer, "Fast Response", "Same-day visits in most areas."],
  [CalendarClock, "Live Booking", "Book any time, pick your slot."],
  [Headset, "Customer Support", "Call or WhatsApp, we're here."],
  [Receipt, "Digital Invoice", "Clear, itemised digital bills."],
  [ShieldCheck, "Warranty", "Up to 180 days on repairs."],
  [Globe2, "All India Coverage", "Major cities across India."],
];

export const WhyChoose = () => (
  <section className="container-x section-y" data-testid="why-choose-section">
    <SectionHead eyebrow="Why Onkaal" title="Why customers choose Onkaal" sub="Built on trust, transparency and on-time service." />
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {WHY.map(([I, t, d], i) => (
        <div key={t} className={`card-soft lift p-6 flex gap-4 ${i === 0 ? "lg:row-span-2 lg:flex-col bg-secondary/60" : ""}`} data-testid={`why-card-${i}`}>
          <span className={`shrink-0 rounded-2xl bg-primary text-primary-foreground grid place-items-center ${i === 0 ? "h-16 w-16" : "h-11 w-11"}`}><I className={i === 0 ? "h-8 w-8" : "h-5 w-5"} /></span>
          <div>
            <h3 className={`font-semibold ${i === 0 ? "text-2xl mt-2" : ""}`}>{t}</h3>
            <p className="text-sm text-muted-foreground mt-1">{d}</p>
            {i === 0 && <p className="text-sm text-muted-foreground mt-4">Every Onkaal partner submits government ID proof and is verified by our team before taking jobs.</p>}
          </div>
        </div>
      ))}
    </div>
  </section>
);

export const HowItWorks = () => (
  <section className="bg-secondary/50 border-y border-border" data-testid="how-it-works-section">
    <div className="container-x section-y">
      <SectionHead eyebrow="How it works" title="Fixed in five simple steps" />
      <ol className="grid md:grid-cols-5 gap-4 relative">
        {STEPS.map(([t, d], i) => (
          <li key={t} className="card-soft p-6 relative fade-up" style={{ animationDelay: `${i * 0.08}s` }} data-testid={`how-step-${i + 1}`}>
            <span className="font-display text-5xl font-extrabold text-primary/15">0{i + 1}</span>
            <h3 className="font-semibold mt-2">{t}</h3>
            <p className="text-sm text-muted-foreground mt-1">{d}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

export const PartnerCTA = () => (
  <section className="container-x section-y" data-testid="partner-cta-section">
    <div className="rounded-[2rem] bg-[#08140E] text-white p-8 sm:p-14 grid lg:grid-cols-2 gap-10 items-center relative overflow-hidden">
      <div className="absolute inset-0 opacity-30 grain" aria-hidden />
      <div className="relative">
        <span className="eyebrow text-emerald-400">Become a Partner</span>
        <h2 className="text-3xl sm:text-5xl font-extrabold mt-3 tracking-tight">Join Onkaal.com</h2>
        <p className="mt-4 text-slate-300 max-w-md">Grow your income with steady jobs near you. Flexible hours, timely payouts and a brand customers trust.</p>
        <Button asChild size="lg" className="mt-8 rounded-full h-12 px-8 bg-emerald-500 hover:bg-emerald-400 text-[#08140E] font-semibold" data-testid="partner-cta-button">
          <Link to="/partner">Become Partner <ArrowRight className="h-4 w-4 ml-1" /></Link>
        </Button>
      </div>
      <div className="relative">
        <p className="text-sm text-slate-400 mb-3">Register as</p>
        <div className="flex flex-wrap gap-2">
          {PROFESSIONS.map((p) => (
            <Link key={p} to={`/partner?profession=${encodeURIComponent(p)}`} className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm hover:bg-emerald-500 hover:text-[#08140E] hover:border-emerald-500 transition-colors" data-testid={`partner-profession-${p.toLowerCase().replace(/\s/g, "-")}`}>{p}</Link>
          ))}
        </div>
      </div>
    </div>
  </section>
);
